import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  countNewSideLoadedSuggestions,
  SideLoadedSuggestionMatcher,
} from "lib/sideLoadedBusinesses/suggestionMatcher";

import {
  getBusinesses,
  getSideLoadedBusinesses,
  selectSideLoadedSuggestionMatcher,
} from "selectors";

// Matching every business takes seconds, so it runs in slices short enough to
// leave the page responsive between them.
const MATCH_SLICE_MS = 12;

interface MatchedNames {
  matcher: SideLoadedSuggestionMatcher;
  recordIdsByName: Map<string, string[]>;
}

const selectNoMatcher = (): null => null;

// Kept outside the component so leaving the page and coming back doesn't match
// every business again - only a new matcher (legacy data has changed) does.
const recordIdsByNameByMatcher = new WeakMap<
  SideLoadedSuggestionMatcher,
  Map<string, string[]>
>();

// Null until enabled and every business has been matched once; while a newer
// matcher (legacy data has changed) is working, the previous counts remain.
export function useSideLoadedSuggestionCountByBusinessId(
  enabled: boolean,
): Record<string, number> | null {
  const matcher: SideLoadedSuggestionMatcher | null = useSelector(
    enabled ? selectSideLoadedSuggestionMatcher : selectNoMatcher,
  );
  const businessById: Record<string, Business> = useSelector(getBusinesses);
  const sideLoadedBusinessById: Record<string, SideLoadedBusiness> =
    useSelector(getSideLoadedBusinesses);

  const [matched, setMatched] = useState<MatchedNames | null>(null);

  useEffect(() => {
    if (!matcher) {
      return undefined;
    }
    let recordIdsByName = recordIdsByNameByMatcher.get(matcher);
    if (!recordIdsByName) {
      recordIdsByName = new Map();
      recordIdsByNameByMatcher.set(matcher, recordIdsByName);
    }
    const pendingNames = Array.from(
      new Set(Object.values(businessById).map(business => business.name)),
    ).filter(name => !recordIdsByName.has(name));

    let timeoutId: ReturnType<typeof setTimeout>;
    let nextName = 0;
    const matchSlice = () => {
      const deadline = performance.now() + MATCH_SLICE_MS;
      while (nextName < pendingNames.length && performance.now() < deadline) {
        const name = pendingNames[nextName];
        recordIdsByName.set(name, matcher.matchIds(name || ""));
        nextName++;
      }
      if (nextName < pendingNames.length) {
        timeoutId = setTimeout(matchSlice, 0);
        return;
      }
      setMatched(previous =>
        previous?.matcher === matcher && !pendingNames.length
          ? previous
          : { matcher, recordIdsByName },
      );
    };
    matchSlice();
    return () => clearTimeout(timeoutId);
  }, [matcher, businessById]);

  return useMemo(() => {
    if (!enabled || !matched) {
      return null;
    }
    return Object.values(businessById).reduce<Record<string, number>>(
      (acc, business) => {
        const recordIds = matched.recordIdsByName.get(business.name);
        if (recordIds) {
          acc[business.id] = countNewSideLoadedSuggestions(
            recordIds,
            sideLoadedBusinessById,
          );
        }
        return acc;
      },
      {},
    );
  }, [enabled, matched, businessById, sideLoadedBusinessById]);
}
